import { useRef, useState, type FormEvent } from 'react';
import { formatBytes } from '../format';
import { adminApi, errorText, type MaterialRow } from './api';
import { confirmAction } from './ui';

/** Materialien einer Lektion oder eines Kurses: Dateien hochladen, Links anlegen, umbenennen, sortieren, löschen. */
export function MaterialsEditor({ courseId, lessonId, materials, onChanged }: { courseId: string; lessonId: string | null; materials: MaterialRow[]; onChanged: () => void }) {
  const [mode, setMode] = useState<'none' | 'link' | 'file'>('none');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  function submitLink(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      await adminApi.addLink(courseId, lessonId, { title: title.trim(), description: description.trim() || null, url: url.trim() });
      setTitle('');
      setDescription('');
      setUrl('');
      setMode('none');
    });
  }

  function chooseFile(file: File) {
    void run(async () => {
      await adminApi.addFile(courseId, lessonId, title.trim() || file.name.replace(/\.[^.]+$/, ''), file);
      setTitle('');
      setMode('none');
    });
  }

  async function move(index: number, dir: -1 | 1) {
    const ids = materials.map((m) => m.id);
    const [id] = ids.splice(index, 1);
    ids.splice(index + dir, 0, id!);
    await run(() => adminApi.reorderMaterials(ids));
  }

  return (
    <div className="materials-editor">
      {materials.length > 0 && (
        <ul className="plain-list materials-editor__list">
          {materials.map((m, i) => (
            <MaterialItem key={m.id} material={m} index={i} count={materials.length} onMove={(dir) => void move(i, dir)} onChanged={onChanged} />
          ))}
        </ul>
      )}
      {mode === 'none' && (
        <div className="row-actions">
          <button type="button" className="button button--secondary button--small" onClick={() => setMode('file')}>Datei hochladen</button>
          <button type="button" className="button button--secondary button--small" onClick={() => setMode('link')}>Link hinzufügen</button>
        </div>
      )}
      {mode === 'link' && (
        <form className="material-form" onSubmit={submitLink}>
          <input type="text" placeholder="Titel" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
          <input type="url" placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} required maxLength={2000} />
          <input type="text" placeholder="Beschreibung (optional)" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
          <div className="row-actions">
            <button type="submit" className="button button--small" disabled={busy}>Link anlegen</button>
            <button type="button" className="link-button" onClick={() => setMode('none')}>Abbrechen</button>
          </div>
        </form>
      )}
      {mode === 'file' && (
        <div className="material-form">
          <input type="text" placeholder="Titel (sonst der Dateiname)" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
          <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) chooseFile(f); }} />
          <div className="row-actions">
            <button type="button" className="button button--small" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Lädt hoch …' : 'Datei wählen und hochladen'}</button>
            <button type="button" className="link-button" onClick={() => setMode('none')}>Abbrechen</button>
          </div>
          <span className="faint small">PDF, Word, Excel, Audio – bis 200 MB. Nur Kunden mit Zugang können die Datei herunterladen.</span>
        </div>
      )}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function MaterialItem({ material: m, index, count, onMove, onChanged }: { material: MaterialRow; index: number; count: number; onMove: (dir: -1 | 1) => void; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(m.title);
  const [description, setDescription] = useState(m.description ?? '');
  const [url, setUrl] = useState(m.url ?? '');
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    try {
      await adminApi.updateMaterial(m.id, { title: title.trim(), description: description.trim() || null, ...(m.kind === 'link' ? { url: url.trim() } : {}) });
      setEditing(false);
      onChanged();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <li className="material-item">
      <span className="material__kind">{m.kind === 'file' ? (m.fileName?.split('.').pop()?.toUpperCase().slice(0, 4) ?? 'Datei') : 'Link'}</span>
      {editing ? (
        <form className="material-form material-form--inline" onSubmit={save}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
          {m.kind === 'link' && <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required />}
          <input type="text" placeholder="Beschreibung" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
          <div className="row-actions">
            <button type="submit" className="button button--small">Speichern</button>
            <button type="button" className="link-button" onClick={() => setEditing(false)}>Abbrechen</button>
          </div>
          {error && <div className="error">{error}</div>}
        </form>
      ) : (
        <div className="material-item__body">
          <strong>{m.title}</strong>
          <span className="muted small">
            {m.kind === 'link' ? m.url : `${m.fileName ?? ''}${m.sizeBytes ? ` · ${formatBytes(m.sizeBytes)}` : ''}`}
            {m.description && ` · ${m.description}`}
          </span>
        </div>
      )}
      {!editing && (
        <div className="adm-row__actions">
          <button type="button" className="icon-button" disabled={index === 0} onClick={() => onMove(-1)} aria-label="Nach oben">↑</button>
          <button type="button" className="icon-button" disabled={index === count - 1} onClick={() => onMove(1)} aria-label="Nach unten">↓</button>
          <button type="button" className="link-button small" onClick={() => setEditing(true)}>Bearbeiten</button>
          <button type="button" className="link-button small link-button--danger" onClick={() => { if (confirmAction(`„${m.title}“ löschen?`)) void adminApi.deleteMaterial(m.id).then(onChanged); }}>Löschen</button>
        </div>
      )}
    </li>
  );
}
